import IntegrationCertificationModal, {
  type IntegrationCertificationCheckbox,
} from '@/components/ui/IntegrationCertificationModal';
import { toaster } from '@/components/ui/toaster';
import {
  useGoogleDriveDisconnect,
  useGoogleDriveRedirectUrl,
} from '@/hooks/actions/use-google-drive-actions';
import { Button } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DriveManageAccessModal from './drive/DriveManageAccessModal';
import IntegrationCard from './IntegrationCard';
import type { IntegrationCardVariant } from './IntegrationCard';
import GoogleDataUseNotice from './GoogleDataUseNotice';

const I18N_PREFIX = 'integrations.googleDrive';
const CALLBACK_PARAM = 'gdrive';
const CERTIFICATION_KEYS = ['scope', 'processor', 'rights'] as const;

interface GoogleDriveIntegrationProps {
  id: string | null;
  isConnected: boolean;
  metadata?: { connectedByEmail?: string; driveRootFolderId?: string | null };
  variant?: IntegrationCardVariant;
  detailHref?: string;
}

const GoogleDriveIntegration = ({
  isConnected,
  metadata,
  variant,
  detailHref,
}: GoogleDriveIntegrationProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const redirectMutation = useGoogleDriveRedirectUrl();
  const disconnectMutation = useGoogleDriveDisconnect();

  const [certificationOpen, setCertificationOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const callbackProcessed = useRef(false);

  useEffect(() => {
    if (callbackProcessed.current) return;
    const urlParams = new URLSearchParams(window.location.search);
    const result = urlParams.get(CALLBACK_PARAM);
    if (!result) return;

    callbackProcessed.current = true;
    window.history.replaceState({}, '', window.location.pathname);

    if (result === 'connected') {
      toaster.create({
        description: t(`${I18N_PREFIX}.success.connected`),
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } else if (result === 'error') {
      toaster.create({
        description: t(`${I18N_PREFIX}.errors.connectionFailed`),
        type: 'error',
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkboxes: IntegrationCertificationCheckbox[] = CERTIFICATION_KEYS.map((key) => ({
    key,
    label: t(`${I18N_PREFIX}.certification.checkboxes.${key}`),
    required: true,
  }));

  const handleToggle = async () => {
    if (isConnected) {
      try {
        await disconnectMutation.mutateAsync();
        toaster.create({
          description: t(`${I18N_PREFIX}.success.disconnected`),
          type: 'success',
        });
      } catch {
        toaster.create({
          description: t(`${I18N_PREFIX}.errors.disconnectFailed`),
          type: 'error',
        });
      }
      return;
    }
    setCertificationOpen(true);
  };

  const handleCertified = async () => {
    try {
      const { redirectUrl } = await redirectMutation.mutateAsync();
      toaster.create({
        description: t(`${I18N_PREFIX}.redirecting`),
        type: 'loading',
      });
      window.location.href = redirectUrl;
    } catch {
      toaster.create({
        description: t(`${I18N_PREFIX}.errors.connectionFailed`),
        type: 'error',
      });
      setCertificationOpen(false);
    }
  };

  const isLoading = redirectMutation.isPending || disconnectMutation.isPending;

  return (
    <>
      <IntegrationCard
        name={t(`${I18N_PREFIX}.name`)}
        description={t(`${I18N_PREFIX}.description`)}
        icon="/images/integrations/google-drive.svg"
        iconStyle="mark"
        isActive={isConnected}
        isLoading={isLoading}
        disabled={isLoading}
        onToggle={handleToggle}
        connectedAs={metadata?.connectedByEmail}
        connectedLabel={t('integrations.connectedLabel')}
        variant={variant}
        detailHref={detailHref}
        disclosure={<GoogleDataUseNotice />}
        detailAction={
          <Button size="sm" variant="ghost" colorPalette="gray" onClick={() => setManageOpen(true)}>
            {t(`${I18N_PREFIX}.sharing.manageAccessCta`)}
          </Button>
        }
      />

      <IntegrationCertificationModal
        isOpen={certificationOpen}
        onClose={() => setCertificationOpen(false)}
        title={t(`${I18N_PREFIX}.certification.title`)}
        description={t(`${I18N_PREFIX}.certification.description`)}
        checkboxes={checkboxes}
        confirmLabel={t(`${I18N_PREFIX}.certification.confirm`)}
        isSubmitting={redirectMutation.isPending}
        onConfirm={handleCertified}
      />

      {manageOpen && <DriveManageAccessModal onClose={() => setManageOpen(false)} />}
    </>
  );
};

export default GoogleDriveIntegration;
